/** @jsxImportSource @emotion/react */
import { faBarcodeRead } from "@fortawesome/pro-regular-svg-icons";
import React, { useCallback, useState } from "react";
import { FormGroup, FormText, Input, Label } from "reactstrap";
import { BarcodeFilter } from "../../actions/Tenants/config/constantsTyped";
import { isFilledArray } from "../../utils";
import { Button } from "../Helper/Button";
import { handlePatternValidation } from "./MetaForm";
import { formatOptionWithMeta } from "./metaOptionLabel";
import { filterOptionWithMeta, normalizeMetaOptions } from "./metaOptions";
import SingleSelect, { OptionType } from "./SingleSelect";
import { QRCodeScan } from "./QRCodeScan";

export interface TextQRcodeScannerProps {
    item: any;
    object: any;
    setValue: (name: string, value: any) => void;
    barcode_filter?: BarcodeFilter;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export const TextQRcodeScanner: React.FC<TextQRcodeScannerProps> = ({
    item,
    object,
    setValue,
    barcode_filter,
    onFocus
}) => {
    const [open, setOpen] = useState(false);

    const handleScan = useCallback(
        (result: string) => {
            setValue(item.name, result);
            setOpen(false);
        },
        [item.name, setValue]
    );

    const onBarcodeButtonClick = useCallback(() => {
        if (barcode_filter) {
            setOpen(!open);
        } else {
            alert("Misconfiguration: Barcode filter not set!"); // eslint-disable-line no-alert
        }
    }, [barcode_filter, open]);

    // * If options are set, and the dropdown is not open, show the single select
    // * E.g. opening the scanner will also enable to put any arbitrary text in the field
    const searchMeta = normalizeMetaOptions(item?.meta_options).length > 0;
    const inputElement = isFilledArray(item?.options) && !open
        ? (
            <SingleSelect
                isCreatable={true}
                unwrapped={true}
                options={item?.options as OptionType[]}
                post_value={object[item.name]}
                field={item.name}
                setValue={setValue}
                formatOptionLabel={searchMeta ? formatOptionWithMeta : undefined}
                filterOption={searchMeta ? filterOptionWithMeta : undefined}
            />
        )
        : (
            <Input
                type="text"
                name={item.name}
                id={item.name}
                value={object[item.name]}
                onChange={(e) => setValue(item.name, e.target.value)}
                invalid={!handlePatternValidation(item, object)}
                onFocus={onFocus}
            />
        );

    return (
        <FormGroup>
            <Label>{item.label}</Label>

            {open && (
                <QRCodeScan
                    onScan={handleScan}
                    barcode_filter={barcode_filter}
                />
            )}

            <FormGroup>
                <div className="d-flex align-items-center w-100 gap-1">
                    <div className="flex-grow-1">
                        {inputElement}
                    </div>

                    <Button
                        color="primary"
                        onClick={onBarcodeButtonClick}
                        icon={faBarcodeRead}
                    />
                </div>

                {!handlePatternValidation(item, object) && item.invalid_hint && (
                    <FormText>{item.invalid_hint}</FormText>
                )}
            </FormGroup>
        </FormGroup>
    );
};
